import { useState } from "react";

import { Button } from "ui/button";
import { Stack } from "ui/stack";
import { TextField } from "ui/text-field";
import toast from "components/partials/toast/toast";

import { useSetIterableApiKey, type ClientAttributes } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";

import { getErrorMessage } from "utilities";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

const MASKED_MIDDLE = "*******";

export const maskSecret = (secret: string) => {
  if (secret.length <= 6) return "*".repeat(secret.length);

  return `${secret.slice(0, 3)}${MASKED_MIDDLE}${secret.slice(-3)}`;
};

export type CollegeProfileIterableApiKeyInputProps = {
  client: ClientAttributes;
};

/**
 * Iterable API key field for the College profile Basic information edit
 * form. Saves through the dedicated `useSetIterableApiKey` mutation rather
 * than the whole-client PUT, and is gated behind `PERM_CLIENT_ADVANCED` - a
 * stricter permission than the rest of Basic information. It owns its own
 * Save/Cancel, independent of the Basic information section's Save/Cancel,
 * shown once the value changes. Lacking `PERM_CLIENT_ADVANCED` renders it
 * `isDisabled`, matching the "Apollo profile code" field beside it.
 *
 * While a key is already saved, the field shows a masked value; focusing it
 * reveals the real value for editing, and blurring without changing it
 * re-masks. This is a display-only concern - `value` always tracks the real
 * key, so Save/Cancel dirty-tracking is unaffected by masking.
 *
 * The section's static read-only display (before "Edit" is clicked) doesn't
 * use this component at all - it renders the value as a plain read-only
 * field like its dl-view siblings (see `CollegeProfileBasicInformationSection`),
 * masked via the exported `maskSecret` helper above.
 */
const CollegeProfileIterableApiKeyInput = ({ client }: CollegeProfileIterableApiKeyInputProps) => {
  const currentUser = useCurrentUser();
  const setIterableApiKeyMutation = useSetIterableApiKey();

  const savedValue = client.iterableApiKey ?? "";
  const isLocked = !!savedValue;
  const hasEditPermission = hasPermission(currentUser, Permission.PERM_CLIENT_ADVANCED);

  const [value, setValue] = useState(savedValue);
  const [isEditable, setIsEditable] = useState(!isLocked);

  const isFieldLocked = isLocked && !isEditable;
  const isDirty = value !== savedValue;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
  }

  function handleFocus() {
    if (!hasEditPermission || isEditable) return;

    setIsEditable(true);
  }

  function handleBlur() {
    if (!isLocked || !isEditable || isDirty) return;

    setIsEditable(false);
  }

  function handleCancel() {
    setValue(savedValue);
    setIsEditable(false);
  }

  async function handleSave() {
    try {
      await setIterableApiKeyMutation.mutateAsync({ id: client.id, iterableApiKey: value });
      toast.success({ data: { title: "Updated Iterable API key" } });
      setIsEditable(false);
    } catch (error) {
      toast.error({
        data: {
          title: "Failed to save Iterable API key",
          message: getErrorMessage(error?.response?.data),
        },
      });
    }
  }

  const fieldProps = {
    name: "iterableApiKey",
    label: "Iterable API key",
    helperText:
      "Please enter API key required to connect this client to associated Iterable project for management of campaign workflows.",
  };

  if (!hasEditPermission) {
    return <TextField {...fieldProps} disabled value={isLocked ? maskSecret(savedValue) : savedValue} />;
  }

  return (
    <TextField
      {...fieldProps}
      value={isFieldLocked ? maskSecret(savedValue) : value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputActions={
        isDirty ? (
          <Stack direction="row" spacing={0.75}>
            <Button
              size="medium"
              disabled={!value || setIterableApiKeyMutation.isPending}
              loading={setIterableApiKeyMutation.isPending}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              size="medium"
              disabled={setIterableApiKeyMutation.isPending}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        ) : undefined
      }
    />
  );
};

export default CollegeProfileIterableApiKeyInput;
